// Every check on the project's runners, in words (queue/machines.rs,
// Poulpe-779/780): who holds each slot and who waits, how long, and how far
// each part has gone. Measured, never evidence or an order.
import type { MachineCheck, MachineEstimate, MachinePart, QueueDetail, QueueMachine } from '../api/types'
import { duration } from './detail'

/** A wait longer than this turns the waiting chip amber. */
export const LONG_WAIT_MS = 10 * 60_000

/** A queue check that has waited this share of its limit turns the chip red. */
export const NEAR_LIMIT = 0.75

/** What the strip's one line says of the checks. */
export interface ChecksSummary {
  running: number
  waiting: number
  /** The longest wait, in ms, of those that wait. */
  longest: number
  tone: 'quiet' | 'warn' | 'danger'
}

export function machines(detail: QueueDetail | null): QueueMachine[] {
  return detail?.machines ?? []
}

/** Whether a machine runs or waits for anything: busy ones open, idle ones fold. */
export function busy(machine: QueueMachine): boolean {
  return machine.slots.some((slot) => slot.running.length > 0 || slot.waiting.length > 0)
}

/** How long a check has waited, up to its start or to now. */
export function waited(check: MachineCheck, measuredAt: string): number {
  const end = Date.parse(check.running_since ?? measuredAt)
  const start = Date.parse(check.waiting_since)
  return Number.isFinite(end) && Number.isFinite(start) ? Math.max(0, end - start) : 0
}

/** Whether a queue check waits close to its limit. */
export function nearLimit(check: MachineCheck, measuredAt: string): boolean {
  return check.source === 'queue' && check.state !== 'running' && check.wait_limit_ms !== null
    && check.wait_limit_ms > 0 && waited(check, measuredAt) >= check.wait_limit_ms * NEAR_LIMIT
}

export function summary(detail: QueueDetail): ChecksSummary {
  let running = 0
  let waiting = 0
  let longest = 0
  let danger = false
  for (const machine of machines(detail)) {
    for (const slot of machine.slots) {
      running += slot.running.length
      waiting += slot.waiting.length
      for (const check of slot.waiting) {
        longest = Math.max(longest, waited(check, detail.measured_at))
        danger ||= nearLimit(check, detail.measured_at)
      }
    }
  }
  const tone = danger ? 'danger' : longest > LONG_WAIT_MS ? 'warn' : 'quiet'
  return { running, waiting, longest, tone }
}

/** The share of a part done, or null when it reports no count to read. */
export function share(part: MachinePart, check: MachineCheck): number | null {
  if (part.state === 'passed' || part.state === 'failed') return 1
  if (part.state === 'to_come') return 0
  const total = check.count?.total
  return total ? Math.min(1, (check.count?.done ?? 0) / total) : null
}

/** A count, short: `212/353 tests`, or `14 crates` with no total. */
export function countText(check: MachineCheck): string | null {
  const count = check.count
  if (!count) return null
  const done = count.total ? `${count.done}/${count.total}` : `${count.done}`
  return `${done} ${count.what}${count.failed ? `, ${count.failed} failed` : ''}`
}

/** What a part's label says: its name and its time, or its count while it runs. */
export function partLabel(part: MachinePart, check: MachineCheck): string {
  if (part.state === 'running') {
    const count = check.count?.total ? `${check.count.done}/${check.count.total}` : null
    return count ? `${part.name} ${count}` : part.name
  }
  if (part.state === 'failed' && check.count?.failed) return `${part.name} · ${check.count.failed} failed`
  if (part.duration_ms != null && part.state === 'passed') return `${part.name} ${duration(part.duration_ms)}`
  return part.name
}

/** Who started a check, as people name it. */
export function sourceText(check: MachineCheck): string {
  switch (check.source) {
    case 'queue': return check.purpose ? `Queue · ${PURPOSE_WORDS[check.purpose]}` : 'Queue'
    case 'agent': return `agent · ${check.via === 'run_check' ? 'run_check' : 'shell'}`
    case 'person': return 'hand'
    case 'unknown': return 'no record'
  }
}

const PURPOSE_WORDS = { landing: 'landing', proposal: 'proposal', hand: 'run_check', idle_main: 'idle main' } as const

/** What a check runs: `test crates/…/queue.rs`, `Whole check`. */
export function whatText(check: Pick<MachineCheck, 'part' | 'target'>): string {
  if (!check.part) return 'A check'
  const part = check.part === 'all' ? 'Whole check' : check.part
  return check.target ? `${part} ${check.target}` : part
}

/** The initial and colour of a check's holder. */
export function avatar(check: MachineCheck): { letter: string; tone: 'claude' | 'codex' | 'person' | 'queue' | 'other' } | null {
  if (check.anonymous) return null
  if (check.source === 'person') return { letter: 'P', tone: 'person' }
  if (check.source === 'queue' && !check.vendor) return null
  if (check.vendor === 'anthropic') return { letter: 'C', tone: 'claude' }
  if (check.vendor === 'openai') return { letter: 'X', tone: 'codex' }
  return check.vendor ? { letter: check.vendor.slice(0, 1).toUpperCase(), tone: 'other' } : null
}

/** The load as people read it: `load 3.1`, and its share of the CPUs. */
export function loadOf(machine: QueueMachine): { text: string; share: number | null } | null {
  if (machine.load_centi === null) return null
  const load = machine.load_centi / 100
  return { text: `load ${load.toFixed(1)}`, share: machine.cpus ? Math.min(1, load / machine.cpus) : null }
}

/** Free disk, in GiB. */
export function diskText(bytes: number | null): string | null {
  if (bytes === null) return null
  const gib = bytes / 2 ** 30
  return `${gib >= 10 ? Math.round(gib) : gib.toFixed(1)} GiB free`
}

// Estimates and the last hour (Poulpe-781). An estimate comes only from
// comparable runs on the machine, and is marked `~`: no number is shown
// without one.

/** An hour, the window of the Last hour tab. */
export const HOUR_MS = 60 * 60_000

/** What an estimate rests on, for a title. */
export function estimateTitle(estimate: MachineEstimate): string {
  const runs = `${estimate.runs} comparable run${estimate.runs === 1 ? '' : 's'}`
  return `Estimated from ${runs}${estimate.confidence === 'low' ? '; a rough guess' : ''}`
}

/** A time of day, `21:31`, on the reader's clock. */
export function clock(at: number): string {
  const date = new Date(at)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/** A running check's time left by its estimate: `~9 min left`; null without one. */
export function leftText(check: MachineCheck, measuredAt: string): string | null {
  if (!check.estimated_end) return null
  const left = Date.parse(check.estimated_end) - Date.parse(measuredAt)
  if (!Number.isFinite(left)) return null
  return left > 0 ? `~${duration(left)} left` : 'past its usual time'
}

/** A waiter's estimated start: `~21:31`; null without one. */
export function startText(check: MachineCheck, measuredAt: string): string | null {
  if (!check.estimated_start) return null
  const at = Math.max(Date.parse(check.estimated_start), Date.parse(measuredAt))
  return Number.isFinite(at) ? `~${clock(at)}` : null
}

/** A running part's usual time, where comparable runs timed it. */
export function usualText(check: MachineCheck): string | null {
  const ms = check.estimate?.part_ms
  return ms != null ? `usual ${duration(ms)}` : null
}

/** Whose colour a check takes in the last hour. */
export type HourTone = 'queue' | 'claude' | 'codex' | 'person' | 'other' | 'failed'

export function toneOf(check: { source: MachineCheck['source']; vendor: string | null; anonymous: boolean }): HourTone {
  if (check.source === 'queue') return 'queue'
  if (check.source === 'person') return 'person'
  if (check.anonymous || check.source === 'unknown') return 'other'
  if (check.vendor === 'anthropic') return 'claude'
  if (check.vendor === 'openai') return 'codex'
  return 'other'
}

/** A stretch of a slot's last hour: from `left`% for `width`%. */
export interface HourSegment { left: number; width: number; tone: HourTone; title: string }

const OUTCOME_WORDS = { passed: 'passed', failed: 'failed', stopped: 'stopped or timed out', gave_up: 'gave up waiting' } as const

function whoText(check: { source: MachineCheck['source']; anonymous: boolean; issue: { key: string } | null }): string {
  if (check.anonymous) return 'outside this workspace'
  if (check.issue) return check.issue.key
  return check.source === 'queue' ? 'Queue' : check.source === 'person' ? 'A person' : 'A check'
}

/** Each slot's last hour: what held it, from the checks that ended and those that run. */
export function hourRows(machine: QueueMachine, measuredAt: string): { slot: string; segments: HourSegment[] }[] {
  const now = Date.parse(measuredAt)
  const from = now - HOUR_MS
  const place = (start: number, end: number) => {
    const left = Math.max(start, from)
    const right = Math.min(end, now)
    return right > left ? { left: (left - from) / HOUR_MS * 100, width: Math.max(0.3, (right - left) / HOUR_MS * 100) } : null
  }
  return machine.slots.map((slot) => {
    const segments: HourSegment[] = []
    for (const ended of slot.recent ?? []) {
      if (!ended.started_at) continue
      const at = place(Date.parse(ended.started_at), Date.parse(ended.ended_at))
      if (!at) continue
      const failed = ended.outcome === 'failed' || ended.outcome === 'stopped'
      segments.push({ ...at, tone: failed ? 'failed' : toneOf(ended), title: `${whoText(ended)} · ${whatText(ended)} · ${OUTCOME_WORDS[ended.outcome]}` })
    }
    for (const check of slot.running) {
      if (!check.running_since) continue
      const at = place(Date.parse(check.running_since), now)
      if (at) segments.push({ ...at, tone: toneOf(check), title: `${whoText(check)} · ${whatText(check)} · running` })
    }
    return { slot: slot.name, segments }
  })
}

/** How many checks waited for a slot of the machine at each of `points` moments of the last hour. */
export function waitingSeries(machine: QueueMachine, measuredAt: string, points = 61): number[] {
  const now = Date.parse(measuredAt)
  const spans: [number, number][] = []
  for (const slot of machine.slots) {
    for (const ended of slot.recent ?? []) spans.push([Date.parse(ended.queued_at), Date.parse(ended.started_at ?? ended.ended_at)])
    for (const check of slot.running) spans.push([Date.parse(check.waiting_since), Date.parse(check.running_since ?? measuredAt)])
    for (const check of slot.waiting) spans.push([Date.parse(check.waiting_since), Infinity])
  }
  return Array.from({ length: points }, (_, index) => {
    const at = now - HOUR_MS + index * HOUR_MS / (points - 1)
    return spans.filter(([start, end]) => start <= at && at < end).length
  })
}

// Where one check stands (Poulpe-782), as run_check's line says it
// (queue/machines.rs, `Standing::line`).

/** One check on its machine: its slot, its place among the waiters, and what is ahead of it. */
export interface Standing {
  machine: string
  slot: string
  check: MachineCheck
  /** Its place among the waiters, from 1; null while it runs. */
  place: number | null
  /** What holds the slot, then the waiters ahead, in order. */
  ahead: MachineCheck[]
}

/** The first check on the project's machines that `mine` picks, running or waiting. */
export function standingOf(detail: QueueDetail | null, mine: (check: MachineCheck) => boolean): Standing | null {
  for (const machine of machines(detail)) {
    for (const slot of machine.slots) {
      const running = slot.running.find(mine)
      if (running) return { machine: machine.name, slot: slot.name, check: running, place: null, ahead: [] }
      const index = slot.waiting.findIndex(mine)
      if (index >= 0) return { machine: machine.name, slot: slot.name, check: slot.waiting[index]!, place: index + 1, ahead: [...slot.running, ...slot.waiting.slice(0, index)] }
    }
  }
  return null
}

export function ordinal(number: number): string {
  const tens = number % 100
  const suffix = tens >= 11 && tens <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[number % 10] ?? 'th'
  return `${number}${suffix}`
}

/** `about 6 min`, rounded up. */
function about(ms: number): string {
  const minutes = Math.ceil(Math.max(0, ms) / 60_000)
  return minutes <= 1 ? 'about a minute' : `about ${minutes} min`
}

const WHOSE = { idle_main: 'idle main', landing: 'a landing', proposal: 'a proposal', hand: 'the queue' } as const

function holderText(check: MachineCheck): string {
  const kind = !check.part || check.part === 'all' ? 'a whole check' : `a ${check.part} check`
  const whose = check.source === 'queue' ? (check.purpose ? WHOSE[check.purpose] : 'the queue')
    : check.source === 'agent' ? 'an agent' : check.source === 'person' ? 'a person' : 'no record'
  return `${kind} (${whose})`
}

function joined(words: string[]): string {
  return words.length <= 1 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
}

/** The line's words: what, where it stands, when (estimated alone), and behind what. */
export function standingWords(standing: Standing, measuredAt: string): { what: string | null; state: string; where: string; when: string | null; behind: string | null } {
  const { check } = standing
  const what = !check.part || check.part === 'all' ? null : check.target ? `${check.part} ${check.target}` : check.part
  const where = `on ${standing.machine} (${standing.slot} slot)`
  const now = Date.parse(measuredAt)
  if (standing.place === null) {
    const end = check.estimated_end ? Date.parse(check.estimated_end) - now : null
    return { what, state: 'runs', where, when: end === null ? null : end > 0 ? `${about(end)} left (estimated)` : 'past its usual time', behind: null }
  }
  const start = check.estimated_start ? Date.parse(check.estimated_start) - now : null
  const holders = standing.ahead.filter((ahead) => ahead.state === 'running').map(holderText)
  const waiters = standing.ahead.filter((ahead) => ahead.state !== 'running')
  const counts = ([['queue', 'queue check', 'queue checks'], ['agent', 'agent’s check', 'agents’ checks'], ['person', 'person’s check', 'people’s checks'], ['unknown', 'check with no record', 'checks with no record']] as const)
    .flatMap(([source, one, many]) => {
      const n = waiters.filter((ahead) => ahead.source === source).length
      return n ? [`${n} ${n === 1 ? one : many}`] : []
    })
  const behind = [...holders, ...counts]
  return { what, state: `${ordinal(standing.place)} in line`, where, when: start === null ? null : `${about(start)} (estimated)`, behind: behind.length ? `behind ${joined(behind)}` : null }
}
